import { Link } from 'react-router-dom';
import { activityKindLabels, callReasonLabels, callResolutionLabels } from '@inbound/contract';
import type {
  ActivityItem,
  CallReason,
  CallResolution,
  CallsByHour,
  KpiTrend,
  RateTrend,
} from '@inbound/contract';

import {
  DASHBOARD_POLL_MS,
  useGetDashboardQuery,
  useGetUsageQuery,
} from '../../store/dashboard-api.js';
import { messageOf } from '../../store/error-message.js';
import { Spinner } from '../../ui/BrandLogo.js';
import { formatDuration } from '../../ui/duration.js';
import { IconAlert, IconCheck, IconClock, IconPhone, IconTrend } from '../../ui/icons.js';
import { ago } from '../../ui/ago.js';
import styles from './DashboardPage.module.css';

/**
 * Dashboard (S11).
 *
 * Five tiles, today's calls by hour, why callers rang and how it ended, and
 * what just happened.
 *
 * ## Why the trend line is a sentence and not an arrow with a number
 *
 * Because the honest version needs words. The server sends what it actually
 * compared against, and the tile says it, so a figure nobody can reproduce
 * never appears. With nothing to compare — a first day, a quiet week — the
 * tile says so rather than claiming an infinite rise or a flat zero.
 */
export function DashboardPage(): JSX.Element {
  // Inbound is live traffic: a call can end, be analysed or need a person at any
  // moment, with nothing to tell the browser. Paused while the tab is hidden,
  // since nobody is reading it then.
  const { data, isLoading, isError, error, refetch } = useGetDashboardQuery(undefined, {
    pollingInterval: DASHBOARD_POLL_MS,
    skipPollingIfUnfocused: true,
  });
  const { data: usage } = useGetUsageQuery();

  if (isLoading) {
    return (
      <section className={styles.view}>
        <div className={styles.state}>
          <span className={styles.loading}>
            <Spinner />
            Loading the dashboard…
          </span>
        </div>
      </section>
    );
  }

  if (isError || data === undefined) {
    return (
      <section className={styles.view}>
        <div className={styles.state}>
          <p>{messageOf(error)}</p>
          <button type="button" className={styles.retry} onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  const week = `/interactions?from=${data.weekFrom}`;

  return (
    <section className={styles.view}>
      <div className={styles.stats}>
        <CountStat icon={<IconPhone />} label="Calls today" trend={data.callsToday} />

        <div className={styles.stat}>
          <div className={styles.statLabel}>
            <span className={styles.liveDot} aria-hidden="true" />
            In progress now
          </div>
          <div className={styles.statValue}>{data.callsInFlight}</div>
          <div className={`${styles.statTrend} ${styles.neutral}`}>
            <span>live, refreshed every 15 seconds</span>
          </div>
        </div>

        <RateStat
          icon={<IconCheck />}
          label="Resolved · 7 days"
          trend={data.resolutionRate}
          better="up"
          of="analysed calls"
        />

        <CountStat
          icon={<IconClock />}
          label="Avg duration · 7 days"
          trend={data.averageDuration}
          format={(seconds) => formatDuration(seconds * 1000)}
          empty="no answered calls"
        />

        <RateStat
          icon={<IconAlert />}
          label="Abandoned · 7 days"
          trend={data.abandonedRate}
          better="down"
          of="finished calls"
        />
      </div>

      <div className={styles.panel}>
        <div className={styles.panelHead}>
          <h3>Calls by hour</h3>
          <span className={styles.sub}>
            {data.callsByHour.typicalWeeks === 0
              ? 'today · a typical day appears after a week of calls'
              : `today against a typical ${data.callsByHour.weekday}`}
          </span>
        </div>
        <HourChart byHour={data.callsByHour} />
      </div>

      <div className={styles.grid}>
        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h3>Recent activity</h3>
            <span className={styles.sub}>newest first</span>
          </div>

          {data.activity.length === 0 ? (
            <div className={styles.empty}>
              <p>Nothing has happened yet. Calls, escalations and agent changes appear here.</p>
            </div>
          ) : (
            <ul className={styles.feed}>
              {data.activity.map((item) => (
                <FeedRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </div>

        <div className={styles.side}>
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3>Why callers rang</h3>
              <span className={styles.sub}>last 7 days</span>
            </div>
            <Bars
              rows={data.reasonBreakdown.map((row) => ({
                key: row.reason,
                label: callReasonLabels[row.reason],
                count: row.count,
                tone: reasonTone(row.reason),
                href: `${week}&reason=${row.reason}`,
              }))}
            />
          </div>

          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3>How calls ended</h3>
              <span className={styles.sub}>last 7 days</span>
            </div>
            <Bars
              rows={data.resolutionBreakdown.map((row) => ({
                key: row.resolution,
                label: callResolutionLabels[row.resolution],
                count: row.count,
                tone: resolutionTone(row.resolution),
                href: `${week}&resolution=${row.resolution}`,
              }))}
            />
          </div>

          {usage !== undefined && (
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <h3>Voice minutes</h3>
                <span className={styles.sub}>{usage.period}</span>
              </div>
              <div className={styles.usage}>
                <div className={styles.usageFigure}>
                  <b>{usage.voiceMinutesUsed.toLocaleString('en-GB')}</b>
                  <span>
                    of {usage.voiceMinutesBudget.toLocaleString('en-GB')} minutes
                    {usage.pctUsed !== null && ` · ${String(usage.pctUsed)}%`}
                  </span>
                </div>
                <div className={styles.track} aria-hidden="true">
                  <span
                    className={`${styles.fill} ${styles[usage.severity.toLowerCase()] ?? ''}`}
                    style={{ width: `${String(Math.min(100, usage.pctUsed ?? 0))}%` }}
                  />
                </div>
                <div className={styles.usageFoot}>
                  {usage.projectedExhaustion !== null && (
                    <span className={styles.warnText}>
                      At this pace the budget runs out around{' '}
                      {new Date(usage.projectedExhaustion).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                      })}
                      .
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** Which way a change is good for, which sets its colour; the arrow shows the direction. */
type Better = 'up' | 'down' | 'neither';

function toneOf(change: number, better: Better): string {
  if (change === 0 || better === 'neither') return 'neutral';
  return change > 0 === (better === 'up') ? 'good' : 'bad';
}

interface CountStatProps {
  icon: JSX.Element;
  label: string;
  trend: KpiTrend;
  format?: (value: number) => string;
  /** What to say when there is no value at all. */
  empty?: string;
}

function CountStat({ icon, label, trend, format, empty }: CountStatProps): JSX.Element {
  const show = format ?? ((value: number) => value.toLocaleString('en-GB'));
  const suffix = `vs ${trend.comparedTo}`;

  let line: JSX.Element;
  if (trend.value === null) {
    line = <span>{empty ?? 'no data yet'}</span>;
  } else if (trend.changePct === null) {
    // No percentage exists, so the tile says what happened instead of rendering
    // a number that would be wrong in both directions.
    line = (
      <span>
        {trend.previous === null || trend.previous === 0
          ? trend.value === 0
            ? `none, and none ${suffix}`
            : `up from none ${suffix}`
          : `no change ${suffix}`}
      </span>
    );
  } else {
    line = (
      <span className={trend.changePct < 0 ? styles.fall : ''}>
        <IconTrend width="13" height="13" />
        {trend.changePct > 0 ? '+' : ''}
        {String(trend.changePct)}% {suffix}
      </span>
    );
  }

  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>
        {icon}
        {label}
      </div>
      <div className={styles.statValue}>{trend.value === null ? '—' : show(trend.value)}</div>
      <div className={`${styles.statTrend} ${styles.neutral}`}>{line}</div>
    </div>
  );
}

interface RateStatProps {
  icon: JSX.Element;
  label: string;
  trend: RateTrend;
  better: Better;
  /** What the rate is a share of, in words: "analysed calls". */
  of: string;
}

function RateStat({ icon, label, trend, better, of }: RateStatProps): JSX.Element {
  const suffix = `vs ${trend.comparedTo}`;
  const change = trend.changePoints;

  return (
    <div className={styles.stat}>
      <div className={styles.statLabel}>
        {icon}
        {label}
      </div>
      <div className={styles.statValue}>
        {trend.value === null ? '—' : `${String(trend.value)}%`}
      </div>
      <div
        className={`${styles.statTrend} ${styles[change === null ? 'neutral' : toneOf(change, better)] ?? ''}`}
      >
        {trend.value === null ? (
          <span>no {of} yet</span>
        ) : change === null ? (
          <span>
            of {trend.of} {of} · nothing to compare {suffix}
          </span>
        ) : (
          <span className={change < 0 ? styles.fall : ''} title={`of ${String(trend.of)} ${of}`}>
            <IconTrend width="13" height="13" />
            {change > 0 ? '+' : ''}
            {String(change)} pts {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Today's calls by hour, against the typical day.
 *
 * Bars for today, a tick for the typical count, so the question "is this busier
 * than usual?" is answered at a glance. Hours still to come have no bar.
 */
function HourChart({ byHour }: { byHour: CallsByHour }): JSX.Element {
  const max = Math.max(1, ...byHour.hours.map((row) => Math.max(row.today ?? 0, row.typical ?? 0)));

  return (
    <div className={styles.chart}>
      <div className={styles.columns} role="img" aria-label="Calls by hour, today">
        {byHour.hours.map((row) => {
          const title =
            `${String(row.hour).padStart(2, '0')}:00 · ` +
            `${row.today === null ? 'still to come' : `${String(row.today)} today`}` +
            `${row.typical === null ? '' : ` · typically ${String(row.typical)}`}`;

          return (
            <div key={row.hour} className={styles.column} title={title}>
              <div className={styles.columnTrack}>
                {row.today !== null && (
                  <span
                    className={styles.columnBar}
                    style={{ height: `${String((row.today / max) * 100)}%` }}
                  />
                )}
                {row.typical !== null && row.typical > 0 && (
                  <span
                    className={styles.typical}
                    style={{ bottom: `${String((row.typical / max) * 100)}%` }}
                  />
                )}
              </div>
              <span className={styles.hourLabel}>{row.hour % 3 === 0 ? row.hour : ''}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.legend}>
        <span>
          <span className={styles.legendBar} aria-hidden="true" /> Today
        </span>
        {byHour.typicalWeeks > 0 && (
          <span>
            <span className={styles.legendTick} aria-hidden="true" /> Typical {byHour.weekday}, last{' '}
            {byHour.typicalWeeks} week{byHour.typicalWeeks === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </div>
  );
}

interface BarRow {
  key: string;
  label: string;
  count: number;
  tone: string;
  href: string;
}

/**
 * A breakdown. Each bar opens the interactions it counted. Unclassified is
 * hidden while empty, since it is a fallback rather than a category.
 */
function Bars({ rows }: { rows: readonly BarRow[] }): JSX.Element {
  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <div className={styles.bars}>
      {rows
        .filter((row) => row.key !== 'UNKNOWN' || row.count > 0)
        .map((row) => (
          <Link key={row.key} to={row.href} className={styles.bar}>
            <div className={styles.barHead}>
              <span>{row.label}</span>
              <b>{row.count}</b>
            </div>
            <div className={styles.barTrack}>
              <span
                className={`${styles.barFill} ${styles[row.tone] ?? ''}`}
                style={{ width: `${String((row.count / max) * 100)}%` }}
              />
            </div>
          </Link>
        ))}
    </div>
  );
}

/** One colour for every reason: they are categories, not verdicts. Only a complaint stands out. */
function reasonTone(reason: CallReason): string {
  return reason === 'COMPLAINT' ? 'complaint' : reason === 'UNKNOWN' ? 'unknown' : 'reason';
}

/** Resolutions are verdicts, so they are coloured as such. */
function resolutionTone(resolution: CallResolution): string {
  if (resolution === 'RESOLVED') return 'resolved';
  if (resolution === 'UNKNOWN') return 'unknown';
  if (resolution === 'UNRESOLVED') return 'complaint';
  if (resolution === 'TRANSFERRED') return 'transferred';
  return 'followUp';
}

function FeedRow({ item }: { item: ActivityItem }): JSX.Element {
  const moment = ago(item.at);

  const body = (
    <>
      <span className={styles.who}>{item.subject}</span> {activityKindLabels[item.kind]}
      {item.detail !== null && <span className={styles.detail}> · {item.detail}</span>}
    </>
  );

  const href =
    item.linkId === null
      ? null
      : item.linkType === 'INTERACTION'
        ? `/interactions/${item.linkId}`
        : item.linkType === 'AGENT'
          ? `/agents/${item.linkId}`
          : null;

  return (
    <li className={styles.feedItem}>
      <span
        className={`${styles.dot} ${styles[item.kind.toLowerCase()] ?? ''}`}
        aria-hidden="true"
      />
      <div className={styles.feedBody}>{href === null ? body : <Link to={href}>{body}</Link>}</div>
      {/* Elapsed time for today, "Yesterday" for yesterday, a date before that —
          and the exact moment on hover, because a relative label reads well and
          cannot be reasoned from. */}
      <span className={styles.feedTime} title={moment.title}>
        {moment.text}
      </span>
    </li>
  );
}
